/**
 * Converts a Firestore timestamp to a formatted date string (DD.MM.YYYY).
 * @param {firebase.firestore.Timestamp} timestamp - The Firestore timestamp to convert.
 * @returns {string} The formatted date string.
 */
export function timestampToDate(timestamp) {
	const date = timestamp.toDate();
	const day = date.getDate().toString().padStart(2, "0");
	const month = (date.getMonth() + 1).toString().padStart(2, "0");
	const year = date.getFullYear();
	return `${day}.${month}.${year}`;
}

/**
 * Formats a Date object into a string in "YYYY-MM-DD" format.
 *
 * @param {Date} date - The date object to format. If the date is not provided, an empty string is returned.
 * @returns {string} A string representing the date in "YYYY-MM-DD" format, or an empty string if the date is invalid.
 */
export function formatDate(date) {
	if (!date) return "";

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

/**
 * Convert a string formatted like dd.MM.yyyy or yyyy-MM-dd into a date object.
 *
 * @param {string} str - The string to convert into a date (accepted formats dd.MM.yyyy | yyyy-MM-dd).
 * @returns {Date} A date object representing the date.
 */
export function stringToDate(str) {
	if (String(str).match(/\d{4}-\d{1,2}-\d{1,2}/)) {
		return new Date(str);
	} else if (String(str).match(/\d{1,2}.\d{1,2}.\d{4}/)) {
		return new Date(formatDateLocal(str));
	}
}

/**
 * Formats a Date object into a string in "DD.MM.YYYY" format.
 *
 * @param {Date} date - The date object to format.
 * @returns {string} A string representing the date in "DD.MM.YYYY" format.
 */
export function formatDateServer(date) {
	if (!date) return "";

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${day}.${month}.${year}`;
}

/**
 * Converts a date string from "DD.MM.YYYY" format to "YYYY-MM-DD" format.
 * Is this useful to create a date object
 *
 * @param {string} date - The date string in "DD.MM.YYYY" format.
 * @returns {string} A string representing the date in "YYYY-MM-DD" format.
 */
export function formatDateLocal(date) {
	if (!date) return "";

	const regex = /(\d+)\.(\d+)\.(\d+)/;
	return date.replace(regex, (match, day, month, year) => {
		return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
	});
}

/**
 * Calculate the day between two dates (startDate and endDate)
 *
 * @param {Date} startDate the start date
 * @param {Date} endDate the end date
 * @returns {number} the number of days between the two dates
 */
export function daysBetweenDates(startDate, endDate) {
	const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
	return diffDays;
}

/**
 * Check if two dates are in the same year
 * 
 * @param {Date} d1 the date to check
 * @param {Date} d2 the date to check
 * @returns {boolean} true if the two dates are in the same year, false otherwise
 */
export const isSameDateYear = (d1, d2) => {
	return d1.getFullYear() === d2.getFullYear();
}

/**
 * Check if two dates from string are in the same year, before check the function transform the string in Date object
 * 
 * @param {string} s1 the date to check
 * @param {string} s2 the date to check
 * 
 * @returns {boolean} true if the two dates are in the same year, false otherwise
 */
export const isSameStringDateYear = (s1, s2) => {
	const d1 = stringToDate(s1);
	const d2 = stringToDate(s2);
	return isSameDateYear(d1, d2);
}
